import config from 'garaje/config/environment';
import { decodeJwt } from 'garaje/utils/encode-jwt';
import { PermissionType } from 'garaje/utils/ui-permissions';
import { isTesting, macroCondition } from '@embroider/macros';
import { RoleId } from 'garaje/utils/custom-roles';
import { GLOBAL_ADMIN } from 'garaje/utils/roles';
import { Response } from 'miragejs';

function loadUser(schema, request) {
  const authHeader = request.requestHeaders.Authorization;
  const authToken = authHeader.replace(/^Bearer /, '');
  const jwt = decodeJwt(authToken);

  let user;
  if (jwt.payload.sub) {
    // JWT includes a user ID; look up user from that
    const matches = jwt.payload.sub.match(/User:(\d+)/);
    if (!matches) return null;
    user = schema.users.find(matches[1]);
  } else if (window.__garajeTests?.user) {
    // JWT did not include a user ID but we've stubbed one in tests
    user = schema.users.find(window.__garajeTests.user.id);
  } else if (macroCondition(isTesting())) {
    // JWT didn't include a user and one wasn't explicitly stubbed in tests;
    // for compatibility, use first user (in tests only)
    user = schema.users.find(1);
  }
  return user;
}

function permissionTypeFromScope(scope) {
  switch (scope) {
    case 'company':
      return PermissionType.COMPANY;
    case 'location':
      return PermissionType.LOCATION;
    case 'zone':
      return PermissionType.ZONE;
    default:
      throw `Unknown role scope "${scope}" - supported values are 'company', 'location', and 'zone'`;
  }
}

export default function () {
  this.urlPrefix = config.authzHost;

  this.get('/a/authz/api/v1/c/:id/ui-permissions', function (schema, request) {
    const permissions = {
      [PermissionType.COMPANY]: {},
      [PermissionType.LOCATION]: {},
      [PermissionType.ZONE]: {},
    };

    const user = loadUser(schema, request);
    if (!user) return permissions;

    const roleAssignments = schema.roleAssignments.where({ userId: user.id });
    if (roleAssignments.length > 0) {
      // If user has one or more authz roles, only give them the permissions for those roles
      for (const roleAssignment of roleAssignments.models) {
        const role = schema['authz/roles'].find(roleAssignment.roleId);
        if (!role) continue;
        const permissionType = permissionTypeFromScope(roleAssignment.roleScopeType);
        const existingPermissions = permissions[permissionType][roleAssignment.roleScopeId] ?? [];
        const newPermissions = [...existingPermissions, ...role.permissions];
        permissions[permissionType][roleAssignment.roleScopeId] = newPermissions;

        // SPECIAL CASE: If user has a "global admin"-type role, give them the same permissions
        // at every location.
        if (role.legacyRole === GLOBAL_ADMIN) {
          const locations = schema.locations.all().models;
          for (const location of locations) {
            const existingLocationPermissions = permissions[PermissionType.LOCATION][location.id] ?? [];
            const newLocationPermissions = [...existingLocationPermissions, ...role.permissions];
            permissions[PermissionType.LOCATION][location.id] = newLocationPermissions;
          }
        }
      }
    } else {
      // SPECIAL CASE: If user has no authz roles but has the legacy "global admin" role
      //  give them all permissions at the company and every location.
      // This is to avoid having to update _every_ test.
      const legacyGlobalAdminRole = schema.companyRoles.findBy({ userId: user.id, roleName: GLOBAL_ADMIN });
      if (legacyGlobalAdminRole) {
        const globalAdminRole = schema['authz/roles'].find(RoleId.GLOBAL_ADMIN);

        permissions[PermissionType.COMPANY][legacyGlobalAdminRole.companyId] = globalAdminRole?.permissions ?? [];

        const locations = schema.locations.all().models;
        for (const location of locations) {
          permissions[PermissionType.LOCATION][location.id] = globalAdminRole?.permissions ?? [];
        }
      }
    }

    return permissions;
  });

  this.get('/a/authz/api/v1/c/:companyId/roles', function (schema /*, request */) {
    return new Response(200, {}, JSON.stringify(schema['authz/roles'].all().models));
  });

  this.get('/a/authz/api/v1/c/1/roles/:roleId', function (schema, request) {
    return schema['authz/roles'].find(request.params.roleId);
  });

  this.get('/a/authz/api/v1/c/1/roles/:roleId/matrix', function () {
    /* This handler just does enough to let the pages in the app work. It doesn't (yet) give real/usable data. */

    const rows = [
      {
        kind: 'grid',
        displayNameKey: 'grid.permissions.name',
        enabled: true,
        locked: false,
        selected: false,
        displayDescriptionKey: 'grid.permissions.description',
        item: null,
      },
      {
        kind: 'product',
        displayNameKey: 'product.visitors.name',
        enabled: true,
        locked: false,
        selected: false,
        displayDescriptionKey: null,
        item: null,
      },
      // TODO: Add actual groups/permission sets
    ];

    return { rows };
  });
}
